import { Box, TextField, Typography, useTheme } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React from "react";
import { FlowStep, MANUAL_INPUT_SOURCES } from "./FlowStepCatalog";
import { SectionCardHeader } from "./FlowStepDebugUI";

export type ManualInput = {
    /** Which manual source the payload comes from; only pasted JSON exists today. */
    sourceId: string;
    subjectId: string;
    artifactJson: string;
};

export const DEFAULT_MANUAL_INPUT: ManualInput = {
    sourceId: MANUAL_INPUT_SOURCES[0].id,
    subjectId: "",
    artifactJson: "",
};

/** Parse error for the pasted artifact, or undefined when it is empty or valid JSON. */
export function manualInputJsonError(input: ManualInput): string | undefined {
    if (!input.artifactJson.trim()) return undefined;
    try {
        JSON.parse(input.artifactJson);
        return undefined;
    } catch (error) {
        return error instanceof Error ? error.message : "Artifact is not valid JSON.";
    }
}

export function isManualInputComplete(input: ManualInput): boolean {
    return (
        input.subjectId.trim().length > 0 &&
        input.artifactJson.trim().length > 0 &&
        manualInputJsonError(input) === undefined
    );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            component="span"
            sx={{ fontSize: 14, fontWeight: 600 }}
        >
            {children}
        </Typography>
    );
}

export function FlowStepDebugManualInput({
    step,
    value,
    onChange,
}: {
    step: FlowStep;
    value: ManualInput;
    onChange: (value: ManualInput) => void;
}) {
    const theme = useTheme();
    const jsonError = manualInputJsonError(value);

    return (
        <>
            <SectionCardHeader
                title="3 · Manual input"
                subtitle={
                    <>
                        The payload{" "}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {step.label}
                        </Box>{" "}
                        will receive. Nothing is read from a run.
                    </>
                }
            />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    px: 2.5,
                    py: 3,
                }}
            >
                <Box sx={{ maxWidth: 480 }}>
                    <AcxSelectSingle
                        id="flow-step-debug-manual-source"
                        inputLabel="Input source"
                        options={MANUAL_INPUT_SOURCES}
                        valueField="id"
                        labelField="label"
                        fullWidth
                        isSearchable={false}
                        defaultValue={MANUAL_INPUT_SOURCES.find(
                            (source) => source.id === value.sourceId,
                        )}
                        onChange={(option?: { id: string }) =>
                            onChange({
                                ...value,
                                sourceId: option?.id ?? value.sourceId,
                            })
                        }
                    />
                </Box>

                <Box sx={{ maxWidth: 480 }}>
                    <FieldLabel>Subject ID</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. 8f21c4de-0a77"
                        value={value.subjectId}
                        onChange={(event) =>
                            onChange({ ...value, subjectId: event.target.value })
                        }
                        inputProps={{ "aria-label": "Subject ID" }}
                        sx={{ mt: 1 }}
                    />
                </Box>

                <Box>
                    <FieldLabel>Artifact</FieldLabel>
                    <Typography
                        component="span"
                        sx={{
                            color: theme.palette.gray[500],
                            fontSize: 13,
                            ml: 1,
                        }}
                    >
                        pasted JSON
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        placeholder="Paste the artifact JSON this step should run against…"
                        value={value.artifactJson}
                        error={jsonError !== undefined}
                        helperText={jsonError}
                        onChange={(event) =>
                            onChange({
                                ...value,
                                artifactJson: event.target.value,
                            })
                        }
                        inputProps={{
                            "aria-label": "Artifact JSON",
                            style: { fontFamily: "monospace", fontSize: 13 },
                        }}
                        sx={{ mt: 1 }}
                    />
                </Box>
            </Box>
        </>
    );
}
